<template>
  <FlashNotifications ref="flash" />

  <div class="min-h-app-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- ── Sticky header ── -->
    <div class="sticky top-14 md:top-0 z-10 flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-3 px-5 py-3.5">

        <div class="flex-shrink-0">
          <div class="flex items-center gap-2">
            <h1 class="text-xl font-bold text-gray-900 dark:text-white">Bug Reports</h1>
            <span v-if="store.page.total_records > 0"
              class="px-2 py-0.5 rounded-full text-xs font-bold bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 tabular-nums">
              {{ store.page.total_records.toLocaleString() }}
            </span>
          </div>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Page {{ store.page.current_page }} of {{ store.page.total_pages }}
          </p>
        </div>

        <div class="flex-1"></div>

        <!-- Status filter -->
        <div class="relative">
          <select
            :value="store.statusFilter"
            @change="store.setStatusFilter($event.target.value)"
            class="pl-3 pr-8 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer appearance-none"
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
          <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
          </svg>
        </div>

        <!-- Refresh -->
        <button @click="store.fetchData()" :disabled="store.loading"
          class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-40">
          <svg class="w-4 h-4" :class="store.loading ? 'animate-spin' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- ── Loading skeleton ── -->
    <div v-if="store.loading && !store.records.length" class="animate-pulse px-5 py-5 space-y-3">
      <div v-for="i in 8" :key="i" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 p-4 flex gap-4">
        <div class="bg-gray-200 dark:bg-gray-700 rounded-full w-8 h-8 flex-shrink-0"></div>
        <div class="flex-1 space-y-2">
          <div class="bg-gray-200 dark:bg-gray-700 rounded h-3 w-40"></div>
          <div class="bg-gray-100 dark:bg-gray-800 rounded h-3 w-72"></div>
        </div>
        <div class="bg-gray-200 dark:bg-gray-700 rounded-full h-5 w-16 flex-shrink-0"></div>
      </div>
    </div>

    <!-- ── Error ── -->
    <div v-else-if="store.error" class="flex flex-col items-center justify-center py-32 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
      <button @click="store.fetchData()" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
        Try again
      </button>
    </div>

    <!-- ── Empty ── -->
    <div v-else-if="!store.loading && !store.records.length" class="flex flex-col items-center justify-center py-32 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.047.83-1.867 1.866-2.013A24.204 24.204 0 0 1 12 12.75Zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 0 1-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44a23.916 23.916 0 0 0 1.152 6.06M12 12.75a2.25 2.25 0 0 0 2.248-2.354M12 12.75a2.25 2.25 0 0 1-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 0 0-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.778 3.778 0 0 1 .4-2.253M12 8.25a2.25 2.25 0 0 0-2.248 2.146M12 8.25a2.25 2.25 0 0 1 2.248 2.146"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">No bug reports</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400">No reports found for the selected filter.</p>
    </div>

    <!-- ── Table ── -->
    <div v-else class="px-5 py-5">
      <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-gray-100 dark:border-gray-700">
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Reporter</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hidden md:table-cell">Page</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Description</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hidden lg:table-cell">Reported</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50 dark:divide-gray-700/50">
            <tr
              v-for="bug in store.records"
              :key="bug.id"
              @click="openDrawer(bug)"
              class="hover:bg-gray-50 dark:hover:bg-gray-700/20 cursor-pointer transition-colors"
            >
              <!-- Reporter -->
              <td class="px-4 py-3.5">
                <div class="flex items-center gap-2.5">
                  <div class="w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                    <svg class="w-3.5 h-3.5 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                    </svg>
                  </div>
                  <span class="text-xs font-medium text-gray-700 dark:text-gray-200 truncate max-w-[120px]">
                    {{ bug.user_email || 'Unknown' }}
                  </span>
                </div>
              </td>

              <!-- Page URL -->
              <td class="px-4 py-3.5 hidden md:table-cell">
                <span class="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[180px] block" :title="bug.page_url">
                  {{ shortenUrl(bug.page_url) }}
                </span>
              </td>

              <!-- Description -->
              <td class="px-4 py-3.5">
                <p class="text-xs text-gray-700 dark:text-gray-300 line-clamp-2 max-w-xs">
                  {{ bug.message || 'No description provided.' }}
                </p>
              </td>

              <!-- Date -->
              <td class="px-4 py-3.5 hidden lg:table-cell">
                <span class="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                  {{ formatDate(bug.created_at) }}
                </span>
              </td>

              <!-- Status badge -->
              <td class="px-4 py-3.5">
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold" :class="statusClass(bug.status)">
                  {{ statusLabel(bug.status) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="store.page.total_pages > 1" class="flex items-center justify-center gap-2 mt-5">
        <button
          v-for="n in store.page.total_pages"
          :key="n"
          @click="store.setPage(n)"
          class="w-8 h-8 rounded-lg text-xs font-semibold transition-colors"
          :class="n === store.page.current_page
            ? 'bg-blue-600 text-white'
            : 'bg-white dark:bg-[#1C2128] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'"
        >{{ n }}</button>
      </div>
    </div>

  </div>

  <!-- ── Detail drawer ── -->
  <Transition
    enter-active-class="transition ease-out duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition ease-in duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0">
    <div v-if="drawerOpen" class="fixed inset-0 bg-black/30 dark:bg-black/50 z-40" @click="closeDrawer" />
  </Transition>

  <Transition
    enter-active-class="transition ease-out duration-300"
    enter-from-class="translate-x-full"
    enter-to-class="translate-x-0"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="translate-x-0"
    leave-to-class="translate-x-full">
    <div v-if="drawerOpen && selectedBug"
      class="fixed right-0 top-0 h-full w-full sm:w-[520px] bg-white dark:bg-[#1C2128] shadow-2xl z-50 flex flex-col">

      <!-- Drawer header -->
      <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
        <div>
          <h2 class="text-base font-bold text-gray-900 dark:text-white">Bug Report</h2>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ formatDate(selectedBug.created_at) }}</p>
        </div>
        <button @click="closeDrawer"
          class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Drawer scrollable body -->
      <div class="flex-1 overflow-y-auto px-5 py-5 space-y-5">

        <!-- Meta info -->
        <div class="grid grid-cols-2 gap-3">
          <div class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3">
            <p class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Reporter</p>
            <p class="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{{ selectedBug.user_email || 'Unknown' }}</p>
          </div>
          <div class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3">
            <p class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">IP Address</p>
            <p class="text-sm font-medium text-gray-800 dark:text-gray-100 font-mono">{{ selectedBug.ip_address }}</p>
          </div>
        </div>

        <!-- Page URL -->
        <div>
          <p class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">Page URL</p>
          <a :href="selectedBug.page_url" target="_blank"
            class="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all">
            {{ selectedBug.page_url }}
          </a>
        </div>

        <!-- Description -->
        <div>
          <p class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">Description</p>
          <p class="text-sm text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
            {{ selectedBug.message || 'No description provided.' }}
          </p>
        </div>

        <!-- Video link -->
        <div v-if="selectedBug.video_link">
          <p class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">Video Link</p>
          <a :href="selectedBug.video_link" target="_blank"
            class="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all">
            {{ selectedBug.video_link }}
          </a>
        </div>

        <!-- Screenshot -->
        <div v-if="selectedBug.image_url">
          <p class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">Screenshot</p>
          <a :href="selectedBug.image_url" target="_blank">
            <img :src="selectedBug.image_url" alt="Screenshot" class="rounded-xl border border-gray-200 dark:border-gray-700 max-h-64 object-contain w-full bg-gray-50 dark:bg-gray-900" />
          </a>
        </div>

        <!-- Divider -->
        <div class="border-t border-gray-100 dark:border-gray-700"></div>

        <!-- Status selector -->
        <div>
          <p class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Status</p>
          <div class="flex gap-2">
            <button
              v-for="opt in STATUS_OPTIONS"
              :key="opt.value"
              @click="draft.status = opt.value"
              class="flex-1 py-2 rounded-xl text-xs font-semibold border transition-all"
              :class="draft.status === opt.value
                ? opt.activeClass
                : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-transparent'"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>

        <!-- Admin notes (rich text via Quill) -->
        <div>
          <p class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Admin Notes</p>
          <div class="border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden bg-white dark:bg-[#22272E]">
            <quill-editor
              :content="draft.admin_notes"
              :enable="true"
              :opts="{ height: '180px' }"
              @update-content="draft.admin_notes = $event"
            />
          </div>
        </div>

      </div>

      <!-- Drawer footer -->
      <div class="flex-shrink-0 px-5 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center gap-3">
        <button @click="closeDrawer"
          class="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors">
          Cancel
        </button>
        <button @click="saveDraft" :disabled="saving"
          class="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-xl transition-colors flex items-center justify-center gap-2">
          <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
          {{ saving ? 'Saving...' : 'Save changes' }}
        </button>
      </div>

    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import { useAdminBugsStore } from 'stores/admin_bugs.js'

const store = useAdminBugsStore()
const flash = ref(null)

const drawerOpen = ref(false)
const selectedBug = ref(null)
const saving = ref(false)

const draft = ref({ status: 'pending', admin_notes: '' })

const STATUS_OPTIONS = [
  {
    value: 'pending',
    label: 'Pending',
    activeClass: 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400',
  },
  {
    value: 'in_progress',
    label: 'In Progress',
    activeClass: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
  },
  {
    value: 'resolved',
    label: 'Resolved',
    activeClass: 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
  },
]

function statusLabel(status) {
  return { pending: 'Pending', in_progress: 'In Progress', resolved: 'Resolved' }[status] ?? status
}

function statusClass(status) {
  return {
    pending:     'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400',
    in_progress: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
    resolved:    'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
  }[status] ?? 'bg-gray-100 text-gray-500'
}

function shortenUrl(url) {
  try {
    const u = new URL(url)
    return u.pathname.length > 30 ? u.pathname.slice(0, 30) + '...' : u.pathname || '/'
  } catch {
    return url
  }
}

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function openDrawer(bug) {
  selectedBug.value = bug
  draft.value = { status: bug.status, admin_notes: bug.admin_notes || '' }
  drawerOpen.value = true
}

function closeDrawer() {
  drawerOpen.value = false
  selectedBug.value = null
}

async function saveDraft() {
  saving.value = true
  const res = await store.updateRecord(selectedBug.value.id, draft.value)
  saving.value = false
  if (!res.js_error && res.response?.success) {
    selectedBug.value = store.records.find(r => r.id === selectedBug.value.id) || selectedBug.value
    flash.value.show('Bug report updated.', 'notice')
  } else {
    flash.value.show('Failed to save changes.', 'alert')
  }
}

onMounted(() => {
  store.fetchData()
})
</script>
